import { concatTypedArrays, reverseBitsInBytes, buf2hex } from "../util";

class FT232H {
    constructor() {
        this.debug = false;
        this.name = "FT232H-based programmer";
        this.description = "An FTDI FT232H based programmer using MPSSE mode for SPI communication.";
        this.device = null;
        this.ready = false;
        this.inEndpointNumber = null;
        this.outEndpointNumber = null;
        this.read_size = 256;
        this.usb_filter = [
            { vendorId: 0x0403, productId: 0x6014 }, // FT232H
            { vendorId: 0x0403, productId: 0x6010 }, // FT2232H
        ]
    }

    static async requestPort() {
        // If you want to request FT2232H as well, you can add both filters here:
        return navigator.usb.requestDevice({
            filters: [
                { vendorId: 0x0403, productId: 0x6014 }, // FT232H
                { vendorId: 0x0403, productId: 0x6010 }, // FT2232H
            ]
        });
    }

    async getDevice() {
        this.ready = false;
        console.log("Requesting device...");
        const device = await FT232H.requestPort();
        this.setupDevice(device);
    }

    async setupDevice(device) {
        this.ready = false;
        try {
            this.device = device;
            console.log("Opening device...");
            await this.device.open();

            console.log("Selecting configuration...");
            await this.device.selectConfiguration(1);

            // For FT2232H (productId = 0x6010), use interfaceNumber = 1 (Channel B)
            // For FT232H (productId = 0x6014), use interfaceNumber = 0 (single channel).
            const interfaceNumber = (device.productId === 0x6010) ? 1 : 0;

            console.log(`Claiming interface ${interfaceNumber}...`);
            await this.device.claimInterface(interfaceNumber);

            console.log("Resetting device...");
            await this.resetDevice(interfaceNumber);

            const interfaceInfo = this.device.configuration.interfaces[interfaceNumber];
            const alternate = interfaceInfo.alternates[0]; // Assuming alternate setting 0
            const outEndpoint = alternate.endpoints.find(e => e.direction === 'out');
            const inEndpoint = alternate.endpoints.find(e => e.direction === 'in');
            this.outEndpointNumber = outEndpoint.endpointNumber;
            this.inEndpointNumber = inEndpoint.endpointNumber;

            console.log("Configuring GPIO...");
            await this.configureGPIO(interfaceNumber);

            console.log("Setting MPSSE mode...");
            await this.setMPSSEMode(interfaceNumber);

            console.log("Synchronizing MPSSE...");
            await this.synchronizeMPSSE();

            console.log("Set latency timer...");
            await this.setLatencyTimer(1);

            this.ready = true;
            console.log("Device setup complete!");
        } catch (error) {
            console.error("Error setting up the device:", error);
            throw error;
        }
    }

    disconnect() {
        this.ready = false;
        if (this.device) {
            this.device.close();
            console.log("Device closed");
        }
    }

    async resetPort(interfaceNumber) {
        const SIO_REQ_SET_BITMODE = 0x0B;
        const BitMode_RESET = 0x00;
        const index = interfaceNumber + 1;
    
        await this.device.controlTransferOut({
            requestType: 'vendor',
            recipient: 'device',
            request: SIO_REQ_SET_BITMODE,
            value: BitMode_RESET,
            index: index
        });
    }
    
    async resetDevice(interfaceNumber) {
        const SIO_REQ_RESET = 0x00;
        const SIO_RESET_SIO = 0x00;
        const index = interfaceNumber + 1;

        await this.device.controlTransferOut({
            requestType: 'vendor',
            recipient: 'device',
            request: SIO_REQ_RESET,
            value: SIO_RESET_SIO,
            index: index
        });
    }

    async setMPSSEMode(interfaceNumber) {
        const SIO_REQ_SET_BITMODE = 0x0B;
        const BitMode_MPSSE = 0x02;
        const index = interfaceNumber + 1;
        const value = (0 & 0xFF) | ((BitMode_MPSSE & 0xFF) << 8);

        await this.device.controlTransferOut({
            requestType: 'vendor',
            recipient: 'device',
            request: SIO_REQ_SET_BITMODE,
            value: value,
            index: index
        });
        await this.device.transferIn(this.inEndpointNumber, 512);
    }

    async synchronizeMPSSE() {
        // Send 0xAA sync byte
        const SEND_0xAA = new Uint8Array([0xAA]);
        await this.device.transferOut(this.outEndpointNumber, SEND_0xAA);

        if (this.debug) {
            console.log('Sent sync byte 0xAA');
        }

        // Read back the response
        const timeout = 500; // milliseconds
        const startTime = Date.now();
        let synced = false;

        while (!synced && (Date.now() - startTime) < timeout) {
            const result = await this.device.transferIn(this.inEndpointNumber, 512);
            const chunk = new Uint8Array(result.data.buffer);
            if (chunk.length < 2) continue;

            for (let i = 0; i < chunk.length - 1; i++) {
                if ((chunk[i] === 0xFA) && (chunk[i + 1] === 0xAA)) {
                    synced = true;
                    break;
                }
            }
        }
        if (!synced) {
            throw new Error("Failed to sync MPSSE");
        }

        if (this.debug) {
            console.log('MPSSE synchronized successfully');
        }
    }

    async setLatencyTimer(latency) {
        const SIO_REQ_SET_LATENCY_TIMER = 0x09;
    
        // The latency value must be between 1 and 255 milliseconds
        if (latency < 1 || latency > 255) {
            throw new Error("Latency timer value must be between 1 and 255 milliseconds.");
        }
    
        await this.device.controlTransferOut({
            requestType: 'vendor',
            recipient: 'device',
            request: SIO_REQ_SET_LATENCY_TIMER,
            value: latency,
            index: 0
        });
    
        if (this.debug) {
            console.log(`Latency timer set to ${latency} ms`);
        }
    }

    async configureGPIO(interfaceNumber) {
        const SET_BITS_LOW = 0x80;

        // Directions: 1 = output, 0 = input
        // D0: SK (clock) - Output
        // D1: DO (MOSI) - Output
        // D2: DI (MISO) - Input
        // D3: CS (chip select) - Output
        this.direction = 0x0B; // 0b00001011 (bits 3,1,0 output)
        this.value_gpio = 0x08; // 0b00001000 (CS high)

        const cmdSetGPIO = new Uint8Array([SET_BITS_LOW, this.value_gpio, this.direction]);

        await this.device.transferOut(this.outEndpointNumber, cmdSetGPIO);
        await this.device.transferIn(this.inEndpointNumber, 2);
        if (this.debug) {
            console.log("GPIO configured: ", buf2hex(cmdSetGPIO));
        }
    }

    async selectChip(select = true) {
        const SET_BITS_LOW = 0x80;

        if (select) {
            // Bring CS low to select the chip
            this.value_gpio &= ~0x08; // Clear bit 3 (CS)
        } else {
            // Bring CS high to deselect the chip
            this.value_gpio |= 0x08; // Set bit 3 (CS)
        }

        const cmdSetCS = new Uint8Array([SET_BITS_LOW, this.value_gpio, this.direction]);
        const SEND_IMMEDIATE = 0x87;
        
        await this.device.transferOut(this.outEndpointNumber, cmdSetCS);
        await this.device.transferOut(this.outEndpointNumber, new Uint8Array([SEND_IMMEDIATE]));
        await this.device.transferIn(this.inEndpointNumber, 512);
        if (this.debug) {
            console.log(`CS ${select ? 'low' : 'high'}: `, buf2hex(cmdSetCS));
        }
    }

    async spiTransfer(write, read_length) {
        if (!this.ready) {
            throw new Error("Device not ready. Call getDevice() first.");
        }

        await this.selectChip(true); // Bring CS low

        const RW_BYTES_PVE_NVE_MSB = 0x31;
        const SEND_IMMEDIATE = 0x87;

        const totalLength = write.length + read_length;
        const lengthLsb = (totalLength - 1) & 0xFF;
        const lengthMsb = ((totalLength - 1) >> 8) & 0xFF;

        const cmdSpiTransfer = new Uint8Array(3 + totalLength);
        cmdSpiTransfer[0] = RW_BYTES_PVE_NVE_MSB;
        cmdSpiTransfer[1] = lengthLsb;
        cmdSpiTransfer[2] = lengthMsb;
        cmdSpiTransfer.set(write, 3);

        // Send the SPI transfer command
        await this.device.transferOut(this.outEndpointNumber, cmdSpiTransfer);

        const r1 = await this.device.transferIn(this.inEndpointNumber, 512);
        const r1chunk = new Uint8Array(r1.data.buffer);

        if (this.debug) {
            console.log("SPI transfer command sent: ", buf2hex(cmdSpiTransfer), r1);
        }

        // If we only got status bytes, send SEND_IMMEDIATE
        if (r1chunk.length === 2) {
            await this.device.transferOut(this.outEndpointNumber, new Uint8Array([SEND_IMMEDIATE]));
            if (this.debug) {
                console.log("Sent SEND_IMMEDIATE command");
            }
        }

        // Read back data
        const expectedBytes = write.length + read_length; 
        let receivedData = new Uint8Array(0);

        // If r1chunk has more than 2 bytes, slice them off
        if (r1chunk.length > 2) {
            receivedData = concatTypedArrays(receivedData, r1chunk.slice(2));
        }

        while (receivedData.length < expectedBytes) {
            const result = await this.device.transferIn(this.inEndpointNumber, 512);
            const chunk = new Uint8Array(result.data.buffer);
            // Some chunks may only hold status bytes
            if (chunk.length === 2) {
                continue;
            }
            receivedData = concatTypedArrays(receivedData, chunk.subarray(2));

            if (this.debug) {
                console.log("Received chunk: ", buf2hex(chunk));
                console.log("Expecting total:", expectedBytes);
                console.log("received data", receivedData);
            }
        }

        const readData = receivedData;

        if (this.debug) {
            console.log("Received data: ", buf2hex(readData));
        }

        await this.selectChip(false); // Bring CS high
        return readData;
    }
}

export { FT232H };
